import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Box, Expand } from "lucide-react";
import { CATEGORIES, PRODUCTS } from "@/data/products";
import ProductModal from "@/components/ProductModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const BADGE_STYLES = {
  "In-House Flagship": "bg-[#263562]/10 text-[#263562] border-[#263562]/25",
  "HTI Distribution": "bg-[#3AB0E1]/10 text-[#1E8FBF] border-[#3AB0E1]/30",
  "Distribution Partner": "bg-[#949CA1]/10 text-[#5F6B78] border-[#949CA1]/30",
};

export default function Products() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState(PRODUCTS);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setProducts(r.data);
      })
      .catch(() => {});
  }, []);

  const visible = products.filter((p) => filter === "all" || p.category === filter);

  return (
    <section id="products" data-testid="products-section" className="relative py-28 lg:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-14"
        >
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#263562]/60">
            The Full Ecosystem
          </span>
          <h2 className="mt-4 font-display font-semibold tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl text-[#263562]">
            Every system your property needs,{" "}
            <span className="text-[#3AB0E1]">one partner</span> to run it.
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#5A6B85] font-light">
            Click any system for the full picture — real screenshots, capabilities
            and fit — right here. No need to leave our site to do your homework.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-14">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={c.testid}
              onClick={() => setFilter(c.id)}
              className={`px-5 py-2.5 rounded-full font-mono-tech text-[11px] uppercase tracking-[0.15em] border transition-all duration-300 ${
                filter === c.id
                  ? "bg-[#263562] text-white border-[#263562] font-bold"
                  : "border-[#263562]/15 text-[#5A6B85] hover:border-[#3AB0E1]/60 hover:text-[#263562]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => {
              const testid = p.testid || `product-card-${p.id}`;
              const badgeCls = BADGE_STYLES[p.badge] || BADGE_STYLES["HTI Distribution"];
              return (
              <motion.article
                key={p.id}
                data-testid={testid}
                layout
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, ease: EASE }}
                onClick={() => setSelected(p)}
                className="group flex flex-col rounded-xl border border-[#263562]/10 bg-white overflow-hidden hover:border-[#3AB0E1]/50 hover:shadow-[0_20px_50px_-15px_rgba(38,53,98,0.18)] transition-all duration-500 cursor-pointer"
              >
                {p.image ? (
                  <div className="relative h-44 overflow-hidden border-b border-[#263562]/10 bg-[#F5F8FC]">
                    <img
                      src={p.image}
                      alt={`${p.name} interface`}
                      loading="lazy"
                      className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-white/60 via-transparent to-transparent" />
                  </div>
                ) : (
                  <div className="relative h-44 border-b border-[#263562]/10 bg-[radial-gradient(circle_at_30%_20%,rgba(58,176,225,0.12),transparent_60%)] flex items-center justify-center">
                    <span className="font-display font-semibold text-5xl text-stroke-navy group-hover:text-[#263562] group-hover:[-webkit-text-stroke:0px] transition-all duration-500">
                      {p.name.split(" ")[0].slice(0, 2).toUpperCase()}
                    </span>
                    <Box className="absolute bottom-4 right-4 w-4 h-4 text-[#C7D2E4]" />
                  </div>
                )}

                <div className="flex flex-col flex-1 p-6">
                  <span
                    className={`self-start rounded-full border px-3 py-1 font-mono-tech text-[9px] uppercase tracking-[0.2em] ${badgeCls}`}
                  >
                    {p.badge}
                  </span>
                  <h3 className="mt-4 font-display font-semibold tracking-tight text-lg sm:text-xl text-[#263562]">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5A6B85] font-light">
                    {p.tagline}
                  </p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-[13px] text-[#5A6B85]">
                        <span className="mt-[7px] w-1 h-1 rounded-full bg-[#3AB0E1] shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <span
                    data-testid={`${testid}-details-button`}
                    className="mt-6 inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#263562] group-hover:text-[#3AB0E1] transition-colors"
                  >
                    <Expand className="w-3.5 h-3.5" />
                    View full details
                  </span>
                </div>
              </motion.article>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>

      <ProductModal product={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
